import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Paper } from '@mui/material';
import { Lock, AutoAwesomeMosaic } from '@mui/icons-material';
import { useModules } from '../../context/ModulesContext';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';

/**
 * Route wrapper that gates a whole module's pages on the tenant's entitlement.
 *
 * The enforceable counterpart to the sidebar hiding a module: the sidebar link
 * disappears, and this stops a direct URL from reaching the page anyway. The
 * API is the real backstop (requireModule → 403), but rendering a locked panel
 * here is a far better experience than letting the page mount and watching
 * every tile error out.
 *
 * While entitlements are still loading we show a spinner rather than gambling
 * on optimistic render, so a genuinely-disabled module never flashes into view.
 */
const ModuleGuard: React.FC<{ module: string; children: React.ReactNode }> = ({ module, children }) => {
  const navigate = useNavigate();
  const { isModuleEnabled, isLoading, modules } = useModules();
  const { user } = useAuth();

  if (isLoading) return <LoadingSpinner />;
  if (isModuleEnabled(module)) return <>{children}</>;

  const descriptor = modules.find((m) => m.key === module);
  const name = descriptor?.name ?? 'This module';
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 520,
          textAlign: 'center',
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            mx: 'auto',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          <Lock sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {name} isn’t enabled yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {descriptor?.tagline
            ? `${descriptor.tagline} `
            : ''}
          {canManage
            ? 'Add it to your plan to switch it on — it takes effect immediately.'
            : 'Ask your workspace owner to add it from Plan & Modules.'}
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AutoAwesomeMosaic />}
            onClick={() => navigate('/admin/modules')}
          >
            Manage plan &amp; modules
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ModuleGuard;
